/**
 * Pipeline: kanban New -> ... -> Orientation. Kartani sudrash = status o'zgartirish.
 * Ko'chirish optimistik (karta darhol o'tadi), server rad etsa karta qaytadi va sabab ko'rsatiladi
 * (02-yechim, 8-band). Job yoki sabab kerak bo'lsa avval oyna ochiladi, keyin yuboriladi.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { useMe } from "@/features/auth/hooks";
import { isManager, type Me } from "@/features/auth/types";
import { driverKeys, useAppSettings, useOpenJobs, usePipeline, useUserOptions } from "@/features/drivers/api";
import {
  PIPELINE_ORDER,
  SOURCE_LABEL,
  type DriverDetail,
  type DriverListItem,
  type DriverStatus,
  type PipelineColumn,
  type StatusChangeInput,
} from "@/features/drivers/types";
import { Badge } from "@/components/ui/badge";
import { FilterSelect, Input } from "@/components/ui/field";
import { ErrorState, PageHeader, PageLoading, SimilarNote } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";
import { PipelineCard, PipelineCardPreview } from "./PipelineCard";
import { moveCard, replaceCard } from "./board";
import { MoveToJobDialog, RejectDialog, type MoveToJobInput, type RejectInput } from "./MoveDialogs";
import type { MenuEntry } from "@/components/ui/context-menu";
import { useDriverActions } from "@/pages/drivers/useDriverActions";
import { ScopePicker } from "@/components/ScopePicker";
import { SectionSwitch } from "@/components/SectionSwitch";

const BOARD_STATUSES = PIPELINE_ORDER.filter((s) => s !== "hired");
const NEEDS_JOB = new Set<DriverStatus>(["submitted", "background_check", "orientation", "hired"]);
const REJECT_ZONE = "reject";
const COLUMN_PREFIX = "col-";
const NO_DRIVERS: DriverListItem[] = [];

interface PipelineData {
  columns: PipelineColumn[];
  total: number;
}

type MoveResult = ApiError | null;

export function PipelinePage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Board me={me.data} />;
}

function Board({ me }: { me: Me }) {
  const { t } = useTranslation("pipeline");
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { branchId } = useBranchFilter();
  const settings = useAppSettings();
  const staleDays = Number(settings.data?.pipeline_stale_days ?? 7);

  // Filtrlar URL'da turadi: sahifani ulashsa yoki yangilasa yo'qolmaydi
  const owner = params.get("owner") ?? "";
  const job = params.get("job") ?? "";
  const source = params.get("source") ?? "";
  const q = params.get("q") ?? "";
  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = useMemo(
    () => ({
      owner: owner || undefined,
      job: job || undefined,
      source: source || undefined,
      q: q || undefined,
      branch: scopedBranch(me, branchId),
      limit: 100,
    }),
    [owner, job, source, q, me, branchId],
  );
  const pipeline = usePipeline(query);

  // Lokal nusxa: sudrash yoki yuborish paytida serverdan kelgan javob kartani "tortib" olmasin
  const [columns, setColumns] = useState<PipelineColumn[] | null>(null);
  const [dragging, setDragging] = useState<DriverListItem | null>(null);
  const pendingRef = useRef(0);
  const justDraggedRef = useRef(false);
  useEffect(() => {
    if (pipeline.data && !dragging && pendingRef.current === 0) setColumns(pipeline.data.columns);
  }, [pipeline.data, dragging]);

  const [moveDialog, setMoveDialog] = useState<{ driver: DriverListItem; to: DriverStatus } | null>(null);
  const [rejectDialog, setRejectDialog] = useState<DriverListItem | null>(null);
  // O'ng tugma menyusi: "Move to" sudrash bilan bir xil (requestMove), "Mark as lost" -- reject oynasi.
  // Funksiyalar barqaror: ustun va kartalar memo, ular har render'da qayta chizilmasin (sudrash silliq bo'lishi uchun)
  const requestMoveRef = useRef(requestMove);
  requestMoveRef.current = requestMove;
  const onMenuMove = useCallback((d: DriverListItem, to: DriverStatus) => requestMoveRef.current(d, to), []);
  const driverActions = useDriverActions({ mode: "pipeline", onMove: onMenuMove, onMarkLost: setRejectDialog });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /** Kartani ustunlar orasida ko'chirish (lokal holat + so'rov keshi); tegilmagan ustunlar o'zgarmaydi */
  const applyMove = useCallback(
    (driver: DriverListItem, to: DriverStatus, override?: DriverListItem) => {
      const moved: DriverListItem = override ?? { ...driver, status: to, status_changed_at: new Date().toISOString(), days_in_stage: 0 };
      setColumns((prev) => (prev ? moveCard(prev, driver.id, to, moved) : prev));
      qc.setQueryData<PipelineData>(driverKeys.pipeline(query), (old) => (old ? { ...old, columns: moveCard(old.columns, driver.id, to, moved) } : old));
    },
    [qc, query],
  );

  /** Serverdan kelgan yangi holat (versiya!) bilan kartani almashtirish */
  const replaceDriver = useCallback((updated: DriverDetail) => {
    setColumns((prev) => (prev ? replaceCard(prev, updated) : prev));
  }, []);

  /**
   * Status o'zgartirish. optimistic=true: avval ko'chiradi, xato bo'lsa qaytaradi.
   * Qaytgan ApiError'ni chaqiruvchi ko'rsatadi (toast yoki oyna ichida).
   */
  const moveDriver = useCallback(
    async (driver: DriverListItem, to: DriverStatus, extra: Partial<StatusChangeInput>, optimistic: boolean): Promise<MoveResult> => {
      const from = driver.status;
      pendingRef.current += 1;
      if (optimistic) applyMove(driver, to);
      try {
        const updated = await api.post<DriverDetail>(`/api/drivers/${driver.id}/status/`, { ...extra, status: to, version: driver.version });
        if (!optimistic) applyMove(driver, to);
        replaceDriver(updated);
        toast.success(`${driver.full_name} → ${tc(`status.${to}`)}`);
        return null;
      } catch (err) {
        if (optimistic) applyMove(driver, from, driver);
        if (err instanceof ApiError) {
          if (err.isConflict) {
            toast.error(t("conflictTitle"), err.changedBy ? t("conflictDesc", { who: err.changedBy, name: driver.full_name }) : t("conflictDescAnon", { name: driver.full_name }));
          }
          return err;
        }
        return new ApiError(0, { code: "network", message: t("network") });
      } finally {
        pendingRef.current -= 1;
        // Har holatda serverdagi haqiqiy holat bilan tenglashtiramiz (sonlar, boshqa o'zgarishlar)
        qc.invalidateQueries({ queryKey: driverKeys.all });
      }
    },
    [applyMove, replaceDriver, qc, t, tc],
  );

  function onDragStart(e: DragStartEvent) {
    const driver = e.active.data.current?.driver as DriverListItem | undefined;
    if (driver) setDragging(driver);
  }

  function onDragEnd(e: DragEndEvent) {
    const driver = e.active.data.current?.driver as DriverListItem | undefined;
    setDragging(null);
    justDraggedRef.current = true;
    window.setTimeout(() => (justDraggedRef.current = false), 100);
    if (!driver || !e.over) return;
    if (e.over.id === REJECT_ZONE) {
      setRejectDialog(driver);
      return;
    }
    const to = e.over.data.current?.status as DriverStatus | undefined;
    if (to) requestMove(driver, to);
  }

  /** Sudrash va o'ng tugma menyusi uchun bitta yo'l: kerak bo'lsa oyna (ish, start sana), aks holda optimistik */
  function requestMove(driver: DriverListItem, to: DriverStatus) {
    if (to === driver.status) return;
    if (to === "hired" || (NEEDS_JOB.has(to) && !driver.current_job)) {
      setMoveDialog({ driver, to });
      return;
    }
    void moveDriver(driver, to, {}, true).then((err) => {
      if (err && !err.isConflict) toast.error(err.message, fieldsSummary(err));
    });
  }

  async function submitMove(input: MoveToJobInput): Promise<MoveResult> {
    if (!moveDialog) return null;
    const err = await moveDriver(moveDialog.driver, moveDialog.to, { job: input.job, note: input.note, start_date: input.start_date }, false);
    if (!err) setMoveDialog(null);
    return err;
  }

  async function submitReject(input: RejectInput): Promise<MoveResult> {
    if (!rejectDialog) return null;
    const err = await moveDriver(rejectDialog, input.status, { reason: input.reason, note: input.note }, false);
    if (!err) setRejectDialog(null);
    return err;
  }

  const openDriver = useCallback(
    (d: DriverListItem) => {
      if (justDraggedRef.current) return;
      navigate(`/drivers/${d.id}`);
    },
    [navigate],
  );

  const hiredCount = columns?.find((c) => c.status === "hired")?.count ?? 0;
  const activeTotal = (columns ?? []).filter((c) => c.status !== "hired").reduce((n, c) => n + c.count, 0);

  return (
    <div className="flex h-[calc(100vh-96px)] flex-col">
      <PageHeader
        title={t("title")}
        subtitle={`${scopeLabel(me, branchId, t)} · ${t("activeDrivers", { count: activeTotal })}`}
        actions={
          <>
            <ScopePicker me={me} />
            <SectionSwitch ariaLabel={tc("nav.drivers")} items={[{ to: "/drivers", label: tc("nav.list") }, { to: "/pipeline", label: tc("nav.board") }]} />
            <HiredTarget count={hiredCount} />
          </>
        }
      >
        <Filters me={me} owner={owner} job={job} source={source} q={q} setParam={setParam} />
      </PageHeader>
      <SimilarNote show={pipeline.data?.fuzzy} />

      {pipeline.isError && !columns ? (
        <ErrorState message={pipeline.error instanceof ApiError ? pipeline.error.message : t("loadFailed")} onRetry={() => pipeline.refetch()} />
      ) : columns === null ? (
        <SkeletonBoard />
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
          <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
            {BOARD_STATUSES.map((status) => {
              const col = columns.find((c) => c.status === status);
              return <Column key={status} status={status} count={col?.count ?? 0} drivers={col?.drivers ?? NO_DRIVERS} staleDays={staleDays} onOpen={openDriver} menu={driverActions.items} />;
            })}
          </div>
          <RejectZone />
          {/* Nusxa `body`da: sahifa o'rami (page-enter animatsiyasi, transform) `position: fixed` uchun konteyner bo'lib
              qolsa ham karta sichqoncha ostida turadi (2026-10-18: nusxa sidebar kengligicha o'ngga siljirdi) */}
          {createPortal(
            <DragOverlay dropAnimation={null}>{dragging ? <PipelineCardPreview driver={dragging} staleDays={staleDays} /> : null}</DragOverlay>,
            document.body,
          )}
        </DndContext>
      )}

      {driverActions.dialogs}
      <MoveToJobDialog driver={moveDialog?.driver ?? null} target={moveDialog?.to ?? "submitted"} onClose={() => setMoveDialog(null)} onSubmit={submitMove} />
      <RejectDialog driver={rejectDialog} onClose={() => setRejectDialog(null)} onSubmit={submitReject} />
    </div>
  );
}

function fieldsSummary(err: ApiError): string | undefined {
  if (!err.fields) return undefined;
  return Object.entries(err.fields)
    .map(([k, v]) => `${k}: ${v.join(" ")}`)
    .join(" ");
}

function scopeLabel(me: Me, branchId: number | null, t: TFunction<"pipeline">): string {
  if (me.role === "recruiter") return t("scope.my");
  // Bir nechta filial: hammasining nomi (menejer filial tanlagan bo'lsa -- o'shanisi)
  if (me.role === "branch_manager") {
    const picked = scopedBranch(me, branchId);
    if (picked) return me.scope_branches.find((b) => b.id === picked)?.name ?? t("scope.myBranch");
    return me.scope_branches.length > 1 ? me.scope_branches.map((b) => b.name).join(", ") : (me.branch?.name ?? t("scope.myBranch"));
  }
  return branchId ? t("scope.branch", { id: branchId }) : t("scope.all");
}

/** memo: sudrash boshlanganda/tugaganda faqat o'zgargan ustun qayta chiziladi */
const Column = memo(function Column({ status, count, drivers, staleDays, onOpen, menu }: { status: DriverStatus; count: number; drivers: DriverListItem[]; staleDays: number; onOpen: (d: DriverListItem) => void; menu: (d: DriverListItem) => MenuEntry[] }) {
  const { t: tc } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_PREFIX}${status}`, data: { status } });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-[200px] min-w-[190px] shrink-0 flex-col rounded-2xl border border-transparent bg-fill/50 transition-colors xl:flex-1",
        isOver && "border-accent/50 bg-accent-soft/60",
      )}
    >
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[13px] font-semibold">{tc(`status.${status}`)}</span>
        <Badge>{count}</Badge>
      </div>
      <ColumnCards count={count} drivers={drivers} staleDays={staleDays} onOpen={onOpen} menu={menu} />
    </div>
  );
});

/** Ustun ichidagi kartalar: useDroppable'ga obuna emas -- ustun ustiga sudralganda ro'yxat qayta chizilmaydi */
const ColumnCards = memo(function ColumnCards({ count, drivers, staleDays, onOpen, menu }: { count: number; drivers: DriverListItem[]; staleDays: number; onOpen: (d: DriverListItem) => void; menu: (d: DriverListItem) => MenuEntry[] }) {
  const { t } = useTranslation("pipeline");
  return (
    <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
      {drivers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-6 text-center text-[12px] text-muted">{t("noDrivers")}</div>
      ) : (
        drivers.map((d) => <PipelineCard key={d.id} driver={d} staleDays={staleDays} onOpen={onOpen} menu={menu} />)
      )}
      {count > drivers.length && <div className="py-1 text-center text-[11px] text-muted">{t("more", { n: count - drivers.length })}</div>}
    </div>
  );
});

function RejectZone() {
  const settings = useAppSettings(); // yo'qotish sababi majburiymi -- zona matni
  const { t } = useTranslation("pipeline");
  const { setNodeRef, isOver } = useDroppable({ id: REJECT_ZONE });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "mt-2 shrink-0 rounded-2xl border-2 border-dashed border-danger/50 bg-danger-soft/50 py-3 text-center text-[12px] font-semibold text-danger transition-colors",
        isOver && "border-danger bg-danger-soft",
      )}
    >
      {settings.data?.lost_reason_required === false ? t("rejectZoneOptional") : t("rejectZone")}
    </div>
  );
}

/** "Hired · N" tugmasi ham tashlash joyi: kartani ustiga tashlasa, hire oynasi ochiladi */
function HiredTarget({ count }: { count: number }) {
  const { t } = useTranslation("pipeline");
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_PREFIX}hired`, data: { status: "hired" as DriverStatus } });
  return (
    <div ref={setNodeRef}>
      <Link
        to="/drivers?status=hired"
        className={cn(
          "inline-flex h-9 items-center rounded-full bg-fill px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-fill-hover",
          isOver && "bg-success-soft text-success ring-2 ring-success/40",
        )}
      >
        {t("hired", { n: count })}
      </Link>
    </div>
  );
}

function Filters({ me, owner, job, source, q, setParam }: { me: Me; owner: string; job: string; source: string; q: string; setParam: (k: string, v: string) => void }) {
  const { t } = useTranslation("pipeline");
  const { t: tc } = useTranslation();
  const showRecruiters = isManager(me.role);
  const recruiters = useUserOptions({ role: "recruiter" }, showRecruiters);
  const jobs = useOpenJobs();
  const [text, setText] = useState(q);
  // Matn filtri 300 ms kechikish bilan URL'ga yoziladi
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (text !== q) setParam("q", text.trim());
    }, 300);
    return () => window.clearTimeout(t);
  }, [text, q, setParam]);
  useEffect(() => setText(q), [q]);

  const recruiterList = recruiters.data?.results ?? [];

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {showRecruiters && (
        <FilterSelect value={owner} onChange={(e) => setParam("owner", e.target.value)} className="w-40" aria-label={t("filters.recruiter")}>
          <option value="">{t("filters.allRecruiters")}</option>
          {recruiterList.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name}
            </option>
          ))}
        </FilterSelect>
      )}
      <FilterSelect value={job} onChange={(e) => setParam("job", e.target.value)} className="w-52" aria-label={t("filters.job")}>
        <option value="">{t("filters.allCarriers")}</option>
        {(jobs.data ?? []).map((j) => (
          <option key={j.id} value={j.id}>
            {j.carrier_name} · {j.title}
          </option>
        ))}
      </FilterSelect>
      <FilterSelect value={source} onChange={(e) => setParam("source", e.target.value)} className="w-36" aria-label={t("filters.source")}>
        <option value="">{t("filters.source")}</option>
        {Object.keys(SOURCE_LABEL).map((k) => (
          <option key={k} value={k}>
            {tc(`source.${k}`)}
          </option>
        ))}
      </FilterSelect>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("filters.search")} className="w-56" aria-label={t("filters.search")} />
      {(owner || job || source || q) && (
        <button
          type="button"
          onClick={() => ["owner", "job", "source", "q"].forEach((k) => setParam(k, ""))}
          className="text-[12px] text-primary hover:underline"
        >
          {tc("actions.clearFilters")}
        </button>
      )}
    </div>
  );
}

function SkeletonBoard() {
  const { t: tc } = useTranslation();
  return (
    <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2">
      {BOARD_STATUSES.map((s) => (
        <div key={s} className="flex w-[200px] min-w-[190px] shrink-0 flex-col rounded-2xl bg-fill/50 xl:flex-1">
          <div className="px-3 py-2 text-[13px] font-semibold text-muted">{tc(`status.${s}`)}</div>
          <div className="space-y-2 px-2 pb-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton h-[76px] rounded-2xl" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
